/**
 * Cliente API - Configuração Base do Axios
 * 
 * CONTEXTO:
 * Arquivo de configuração centralizada para chamadas HTTP ao backend.
 * Define a instância Axios com configurações padrão.
 * 
 * RESPONSABILIDADES:
 * - Criar instância Axios configurada
 * - Definir baseURL a partir de variáveis de ambiente
 * - Configurar headers padrão
 * - Configurar interceptors (futuro: autenticação, tratamento de erros)
 * 
 * USO:
 * Todos os serviços de API devem importar e usar esta instância.
 */

import axios from 'axios';

/**
 * Obter URL base da API a partir de variáveis de ambiente
 * 
 * VITE_API_URL deve ser definida no arquivo .env
 * Fallback: http://localhost:8000 para desenvolvimento local
 */
const URL_BASE_API = import.meta.env.VITE_API_URL || 'http://localhost:8000';

/**
 * Instância Axios configurada para comunicação com backend
 * 
 * CONFIGURAÇÕES:
 * - baseURL: URL base da API (variável de ambiente)
 * - timeout: 30 segundos (análises podem demorar)
 * - headers: Content-Type application/json
 * 
 * INTERCEPTORS (futuro):
 * - Request: adicionar token de autenticação
 * - Response: tratamento global de erros
 */
export const clienteApi = axios.create({
  baseURL: URL_BASE_API,
  timeout: 30000, // 30 segundos
  headers: {
    'Content-Type': 'application/json',
  },
});
/**
 * Request interceptor: adiciona o token JWT (se presente) a cada requisição.
 *
 * IMPLEMENTAÇÃO:
 * - Busca o token sempre com `localStorage.getItem()` (garante o token mais recente)
 * - Se existir, adiciona o header `Authorization: Bearer <token>` ao objeto `config.headers`
 * - Se não existir, não altera os headers (permite requests públicos)
 * - Adiciona headers de telemetria (X-Session-ID, X-Request-ID, X-Frontend-Version)
 *
 * NOTAS DE SEGURANÇA:
 * - Não logar o token completo em produção
 */

// Constantes para telemetria
const STORAGE_KEY_SESSION_ID = '@workforce-juridico:session_id';
const FRONTEND_VERSION = '1.0.0'; // TODO: extrair de package.json

/**
 * Obtém ou cria um session_id único para a sessão do navegador
 */
function obterSessionId(): string {
  let sessionId = sessionStorage.getItem(STORAGE_KEY_SESSION_ID);
  if (!sessionId) {
    sessionId = crypto.randomUUID();
    sessionStorage.setItem(STORAGE_KEY_SESSION_ID, sessionId);
  }
  return sessionId;
}

clienteApi.interceptors.request.use(
  (config) => {
    try {
      // Garantir que headers exista
      if (!config.headers) {
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        config.headers = {};
      }

      // ===== HEADERS DE TELEMETRIA =====
      // Adicionar headers para correlação de logs
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      config.headers['X-Session-ID'] = obterSessionId();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      config.headers['X-Request-ID'] = crypto.randomUUID();
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      config.headers['X-Frontend-Version'] = FRONTEND_VERSION;

      // ===== TOKEN DE AUTENTICAÇÃO =====
      // Buscar token mais recente do localStorage
      const token = localStorage.getItem('@workforce-juridico:token');

      if (token) {
        // Adicionar header Authorization sem expor o token em logs
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        config.headers.Authorization = `Bearer ${token}`;

        if (import.meta.env.DEV) {
          // Ambiente de desenvolvimento: indicador leve (não imprime token)
           
          console.debug('[clienteApi] Authorization header adicionada');
        }
      }

      return config;
    } catch {
      // Em caso de erro inesperado, não bloquear a request
      return config;
    }
  },
  (error) => Promise.reject(error)
);

/**
 * Response interceptor: tratamento global de erros HTTP
 *
 * REQUISITOS:
 * - Capturar 401 (não autorizado) -> tentar refresh token
 * - Se refresh falhar -> limpar localStorage e redirecionar para /login?sessao-expirada=true
 * - Manter comportamento padrão para outros erros
 */

// Flag para evitar múltiplas tentativas de refresh simultâneas
let isRefreshing = false;
// Fila de requests que aguardam o refresh
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: Error) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else if (token) {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

clienteApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Verificar se é erro 401 e não é uma retry
    if (error?.response?.status === 401 && !originalRequest._retry) {
      // Não tentar refresh para o próprio endpoint de refresh ou login
      const url = originalRequest?.url || '';
      if (url.includes('/refresh') || url.includes('/login')) {
        // Limpar e redirecionar
        try {
          localStorage.removeItem('@workforce-juridico:token');
          localStorage.removeItem('@workforce-juridico:refresh_token');
          localStorage.removeItem('@workforce-juridico:usuario');
        } catch { /* ignore */ }
        window.location.href = '/login?sessao-expirada=true';
        return Promise.reject(error);
      }

      if (isRefreshing) {
        // Se já está fazendo refresh, adicionar à fila
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers.Authorization = `Bearer ${token}`;
            return clienteApi(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      originalRequest._retry = true;
      isRefreshing = true;

      const refreshToken = localStorage.getItem('@workforce-juridico:refresh_token');

      if (!refreshToken) {
        // Sem refresh token, redirecionar para login
        isRefreshing = false;
        try {
          localStorage.removeItem('@workforce-juridico:token');
          localStorage.removeItem('@workforce-juridico:usuario');
        } catch { /* ignore */ }
        window.location.href = '/login?sessao-expirada=true';
        return Promise.reject(error);
      }

      try {
        // Tentar renovar o token
        const response = await axios.post(
          `${clienteApi.defaults.baseURL}/api/core/usuarios/refresh`,
          { refresh_token: refreshToken }
        );

        const newAccessToken = response.data.access_token;

        // Atualizar token no localStorage
        localStorage.setItem('@workforce-juridico:token', newAccessToken);

        if (import.meta.env.DEV) {
          console.info('[clienteApi] Token renovado com sucesso via refresh token');
        }

        // Processar fila de requests pendentes
        processQueue(null, newAccessToken);

        // Repetir request original com novo token
        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
        return clienteApi(originalRequest);

      } catch (refreshError) {
        // Refresh falhou, limpar tudo e redirecionar
        processQueue(new Error('Refresh token expirado'), null);

        if (import.meta.env.DEV) {
          console.info('[clienteApi] Refresh token expirado ou inválido. Redirecionando para login.');
        }

        try {
          localStorage.removeItem('@workforce-juridico:token');
          localStorage.removeItem('@workforce-juridico:refresh_token');
          localStorage.removeItem('@workforce-juridico:usuario');
        } catch { /* ignore */ }

        window.location.href = '/login?sessao-expirada=true';
        return Promise.reject(refreshError);

      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  }
);

